import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, IconButton, Menu, MenuItem, Typography, useMediaQuery } from "@mui/material";
import VideoStream from "./VideoStream";
import SessionTracking from "./SessionTracking";
import { makeStyles } from "@mui/styles";
import ImageCarousel from "../../components/ImageCarousel";
import { useParams } from "react-router-dom";
import TimelineSlider from "./TimelineSlider";
import { useSelector } from "react-redux";
import SettingsIcon from "@mui/icons-material/Settings";
import { expand_timeline } from "../../services/globalService";

const useStyles = makeStyles({
  notAvailable: {
    border: "1px solid #C4C4C4",
    borderRadius: "4px",
    padding: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: "20vh",
  },
  roletype: {
    fontFamily: "Roboto !important",
    fontStyle: "normal",
    fontWeight: "500 !important",
    fontSize: "16px !important",
    lineHeight: "24px !important",
    color: " #787878",
    paddingTop: "10px",
  },
  timelineMainContainer: {
    display: "flex",
    justifyContent: "left",
    alignItems: "center",
    marginTop: "20px !important",
  },
  issueTypeContainer: {
    display: "flex",
    alignItems: "baseline !important",
    gap: "10px !important",
    fontSize: "12px !important",
    color: "grey",
    fontWeight: "600 !important",
    marginLeft: "3px !important",
    padding: "6px !important",
  },
  circle: {
    width: "10px !important",
    height: "10px !important",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
});

const VideoAndSessionTracking = ({ videoStream, cameraCapture, sessionData, type }) => {
  const classes = useStyles();
  const matches = useMediaQuery("(min-width:900px)");
  const roleState = useSelector((state) => state.role);

  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState("");
  const { id, accessCode } = useParams();
  // const timelineData = roleState?.updateTimelineEventsSuccess?.timeline || sessionData?.timeline;
  const [timelineData, setTimelineData] = useState(sessionData?.timeline);

  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [compact, setCompact] = useState(true);
  const [hidePrimary, setHidePrimary] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const open = Boolean(anchorEl);
  const gridRef = useRef(null);

  async function fetchVideoStream() {
    try {
      let newfileName = btoa(videoStream.fileName);
      const src = `${process.env.REACT_APP_API_URL}/sessionTracking/videoStream/${id}/${accessCode}?v=${newfileName}`;
      videoRef.current.src = src;
      setVideoSrc(src);
    } catch (error) {
      console.error("Error fetching video stream:", error);
    }
  }

  useEffect(() => {
    fetchVideoStream();
    // eslint-disable-next-line
  }, []);

  // useEffect for wheel event listener
  useEffect(() => {
    const handleWheel = (e) => {
      if (gridRef.current) {
        e.preventDefault();
        gridRef.current.scrollLeft += e.deltaY + e.deltaX;
      }
    };

    const gridElement = gridRef.current;
    if (gridElement) {
      gridElement.addEventListener("wheel", handleWheel);
    }

    return () => {
      if (gridElement) {
        gridElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, []);

  useEffect(() => {
    const handleLoadedMetadata = () => {
      setVideoDuration(videoRef.current.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(videoRef.current.currentTime);
    };

    const currentVideoRef = videoRef.current;

    if (currentVideoRef) {
      currentVideoRef.addEventListener("loadedmetadata", handleLoadedMetadata);
      currentVideoRef.addEventListener("timeupdate", handleTimeUpdate);
    }

    return () => {
      if (currentVideoRef) {
        currentVideoRef.removeEventListener("loadedmetadata", handleLoadedMetadata);
        currentVideoRef.removeEventListener("timeupdate", handleTimeUpdate);
      }
    };
  }, []);

  const handleTimelineChange = (newTime) => {
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleHidePrimary = () => {
    setHidePrimary(!hidePrimary);
    setCompact(true);
    setSelectedEvents([]);
    handleClose();
  };

  const handleCompactify = () => {
    setCompact(!compact);
    handleClose();
  };

  const handleClear = () => {
    setSelectedEvents([]); // Clear selected events in the parent
    handleClose();
  };

  useEffect(() => {
    if (roleState?.updateTimelineEventsSuccess?.timeline) {
      setTimelineData(roleState?.updateTimelineEventsSuccess?.timeline);
    }
  }, [roleState?.updateTimelineEventsSuccess?.timeline]);

  // Use useMemo to memoize the counts
  const { warningCount, errorCount, nonIssueCount } = useMemo(() => {
    let warningCount = 0;
    let errorCount = 0;
    let nonIssueCount = 0;

    if (roleState?.updateTimelineEventsSuccess?.timeline) {
      roleState?.updateTimelineEventsSuccess?.timeline?.forEach((event) => {
        if (event.color === "warning") {
          warningCount++;
        } else if (event.color === "error") {
          errorCount++;
        } else if (event.color === "success") {
          nonIssueCount++;
        }
      });
    } else {
      // Loop through the array and count occurrences of each color
      timelineData?.forEach((event) => {
        if (event.color === "warning") {
          warningCount++;
        } else if (event.color === "error") {
          errorCount++;
        } else if (event.color === "success") {
          nonIssueCount++;
        }
      });
    }

    return { warningCount, errorCount, nonIssueCount };
  }, [timelineData, roleState?.updateTimelineEventsSuccess?.timeline]);

  // Apply expand_timeline function
  const { newTimeline, scaleFactor } = useMemo(() => {
    return expand_timeline(timelineData, !compact, type);
  }, [timelineData, compact, type]);

  console.log("expand_timeline", newTimeline, scaleFactor);

  return (
    <Grid>
      <Grid container>
        <Grid item xs={matches ? 5 : 12}>
          {videoStream?.fileName ? (
            <VideoStream videoRef={videoRef} videoSrc={videoSrc} />
          ) : !cameraCapture || Object.keys(cameraCapture)?.length === 0 ? (
            <Grid className={classes.notAvailable}>
              <Typography>No video/images available.</Typography>
            </Grid>
          ) : (
            <>
              {cameraCapture && (
                <ImageCarousel
                  images={Object.values(cameraCapture?.images).map((imageData) => ({
                    url: imageData.url,
                    createdAt: imageData.createdAtUtc,
                  }))}
                />
              )}
            </>
          )}
        </Grid>
        <Grid item xs={matches ? 1 : 0}></Grid>
        <Grid item xs={matches ? 6 : 12} sx={{ marginBottom: "30px" }}>
          {sessionData ? (
            <SessionTracking sessionData={sessionData} />
          ) : (
            <Grid className={classes.notAvailable} sx={matches ? null : { mt: 2 }}>
              <Typography>No data available</Typography>
            </Grid>
          )}
        </Grid>
        {sessionData?.timeline && (
          <>
            <Grid item xs={12} className={classes.timelineMainContainer}>
              {errorCount > 0 && (
                <Grid item xs={2.5}>
                  <Typography className={classes.issueTypeContainer}>
                    {" "}
                    <span
                      className={classes.circle}
                      style={{
                        backgroundColor: "#F44336",
                      }}
                    ></span>
                    {errorCount} {errorCount === 1 ? "Severe Issue" : "Severe Issues"}
                  </Typography>
                </Grid>
              )}
              {warningCount > 0 && (
                <Grid item xs={2.5}>
                  <Typography className={classes.issueTypeContainer}>
                    {" "}
                    <span
                      className={classes.circle}
                      style={{
                        backgroundColor: "#FF941C",
                      }}
                    ></span>
                    {warningCount} {warningCount === 1 ? "Moderate Issue" : "Moderate Issues"}
                  </Typography>
                </Grid>
              )}
              {nonIssueCount > 0 && (
                <Grid item xs={2.5}>
                  <Typography className={classes.issueTypeContainer}>
                    {" "}
                    <span
                      className={classes.circle}
                      style={{
                        backgroundColor: "#6AA84F",
                      }}
                    ></span>
                    {nonIssueCount} {nonIssueCount === 1 ? "Marked as non-issue" : "Marked as non-issues"}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {!videoStream?.fileName && <Grid item xs={12} sx={{ mt: 2 }}></Grid>}
            <Grid item xs={12} sx={{ position: "relative" }}>
              <IconButton sx={{ position: "absolute", right: 0, zIndex: 1 }}>
                <SettingsIcon
                  fontSize="small"
                  sx={{ color: "#757575", p: 0, cursor: "pointer", ":hover": { color: "#616161" } }}
                  onClick={handleClick}
                />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
              >
                <MenuItem onClick={handleHidePrimary}>{hidePrimary ? "Show Questions" : "Hide Questions"}</MenuItem>
                <MenuItem onClick={handleCompactify}>{compact ? "Expand Timeline" : "Compactify Timeline"}</MenuItem>
                <MenuItem onClick={handleClear}>Clear</MenuItem>
              </Menu>
            </Grid>
            <Grid
              item
              xs={12}
              ref={gridRef}
              sx={{
                width: "100%",
                position: "relative",
                borderRadius: "15px",
                overflow: "auto",
                bgcolor: "#E0E0E0",
                clipPath: "fill-box",
                // scrollbar styles
                "&::-webkit-scrollbar": {
                  height: "2px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "darkgrey",
                  borderRadius: "2px",
                },
                "&:hover::-webkit-scrollbar-thumb": {
                  backgroundColor: "grey",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <TimelineSlider
                currentTime={currentTime}
                onTimeChange={handleTimelineChange}
                videoDuration={videoDuration}
                events={newTimeline}
                sessionData={sessionData}
                hidePrimary={hidePrimary}
                compact={compact}
                selectedEvents={selectedEvents}
                setSelectedEvents={setSelectedEvents}
                scaleFactor={scaleFactor}
                type={type}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default VideoAndSessionTracking;
